import { useCallback, useEffect, useRef, useState } from 'react';
import { buildingsApi, CeebImportStatusResponse } from '../services/api';

// Status widziany przez UI:
//  - idle     — nic jeszcze nie ruszyło,
//  - running  — trwa import / polling (także idle/brak-pliku po stronie backendu),
//  - done/error — terminal.
// Zgodny z enum §6 planu (idle/running = NIE-terminalne, done/error = terminalne).
export type CeebImportUiStatus = 'idle' | 'running' | 'done' | 'error';

export interface UseCeebImportPolling {
  status: CeebImportUiStatus;
  result: CeebImportStatusResponse | null;
  // Startuje import dla budynku: 202 → polling co 3 s aż do świeżego terminala.
  start: (buildingId: string) => Promise<void>;
}

// Wzorzec CeebDemoButton.tsx:105 — poll co 3 s.
const POLL_INTERVAL_MS = 3000;
// Twardy timeout 180 s = 60 pollów — chroni przed zawisłym subprocesem lub
// utratą pliku statusu (R4); bez tego polling kręciłby się w nieskończoność.
const MAX_POLL_MS = 180000;

/**
 * Współdzielony hook pollingu importu CEEB (DRY, wzorzec CeebDemoButton z PEŁNYM
 * guardem `previousFinishedAtRef`). Naiwne "koniec gdy status !== 'running'" jest
 * WYŚCIGOWE (luka F2):
 *   1) między 202 a pierwszym zapisem subprocesu GET zwraca {status:'idle'} — nie wolno
 *      tego traktować jako "done",
 *   2) "Odśwież" mógłby odczytać STARY `done` z poprzedniego runu, zanim nowy subprocess
 *      nadpisze plik.
 * Reguła (jak CeebDemoButton): terminal TYLKO gdy status ∈ {done,error} ORAZ `finishedAt`
 * jest OBECNY i RÓŻNY od `previousFinishedAtRef` (świeży terminal nowszy niż start runu).
 */
export function useCeebImportPolling(): UseCeebImportPolling {
  const [status, setStatus] = useState<CeebImportUiStatus>('idle');
  const [result, setResult] = useState<CeebImportStatusResponse | null>(null);

  const pollRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  // Trzyma finishedAt OSTATNIO zaakceptowanego terminala. Zasiewany w start() z
  // ewentualnego POPRZEDNIEGO runu, by stary `done` w pliku statusu nie został wzięty
  // za wynik bieżącego importu (anty-stale, F2).
  const previousFinishedAtRef = useRef<string | null | undefined>(undefined);

  const clearPoll = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Sprzątanie przy unmount — gwarantuje clearInterval, brak setState po odmontowaniu.
  useEffect(() => clearPoll, [clearPoll]);

  const startPolling = useCallback(
    (buildingId: string) => {
      clearPoll();
      pollRef.current = window.setInterval(async () => {
        // Twardy timeout liczony od momentu start().
        if (Date.now() - startedAtRef.current >= MAX_POLL_MS) {
          clearPoll();
          setResult({ status: 'error', error: 'timeout' });
          setStatus('error');
          return;
        }
        try {
          const data = await buildingsApi.importStatus(buildingId);
          const isTerminal = data.status === 'done' || data.status === 'error';
          // Świeży terminal = done/error + finishedAt obecny i RÓŻNY od poprzedniego.
          const isFresh =
            isTerminal &&
            data.finishedAt != null &&
            data.finishedAt !== previousFinishedAtRef.current;
          // idle/running/brak-pliku ORAZ terminal ze STARYM/null finishedAt → poll dalej.
          if (!isFresh) return;
          previousFinishedAtRef.current = data.finishedAt;
          clearPoll();
          setResult(data);
          setStatus(data.status as CeebImportUiStatus);
        } catch (_) {
          // Błąd pojedynczego polla (sieć/parsowanie) — spróbuj ponownie w kolejnym ticku.
        }
      }, POLL_INTERVAL_MS);
    },
    [clearPoll]
  );

  const start = useCallback(
    async (buildingId: string) => {
      clearPoll();
      setStatus('running');
      setResult(null);
      startedAtRef.current = Date.now();

      // Baseline: zapamiętaj finishedAt ewentualnego POPRZEDNIEGO runu, zanim subprocess
      // nadpisze plik statusu — dzięki temu stary `done` nie zostanie wzięty za wynik.
      try {
        const baseline = await buildingsApi.importStatus(buildingId);
        previousFinishedAtRef.current = baseline?.finishedAt ?? undefined;
      } catch (_) {
        previousFinishedAtRef.current = undefined;
      }

      // 202 — wystartuj subprocess importu.
      try {
        await buildingsApi.importFromCeeb(buildingId);
      } catch (e: any) {
        clearPoll();
        setResult({ status: 'error', error: e?.message || 'Nie udało się uruchomić importu' });
        setStatus('error');
        return;
      }

      startPolling(buildingId);
    },
    [clearPoll, startPolling]
  );

  return { status, result, start };
}
